import { useEffect } from 'react'

/**
 * Auto-resize handshake for framed embeds.
 *
 * Measures this document's height and posts it to the host page so the host's
 * tiny listener script can resize the iframe to fit its content — no fixed
 * height, no inner scrollbar, mobile-friendly. No-op when not framed, and
 * harmless for hosts without a listener (they just ignore the message), so it's
 * safe for every embed already pasted with a fixed height.
 *
 * Robustness (why this isn't a one-shot measure):
 *   The height can grow AFTER the first paint — the biggest culprit is the
 *   grouping's custom branding FONT swapping in (webfonts load async and reflow
 *   headings/body taller), plus lazy images and async content. If we post the
 *   height before that settles, the iframe lands a few px short and the bottom
 *   clips. So we re-measure on: ResizeObserver(body), window resize/load, image
 *   load (capture), document.fonts.ready, and a short settle loop over ~4s. We
 *   also fold in the last element's getBoundingClientRect().bottom because
 *   scrollHeight can miss a trailing margin. Posts are de-duped (only when the
 *   height actually changed) so the extra passes are cheap.
 */
export default function useAutoResizeToParent() {
  useEffect(() => {
    if (window === window.parent) return
    let raf = 0
    let lastPosted = 0

    const measure = () => {
      const b = document.body
      const d = document.documentElement
      // Trailing margins on the last flow element are excluded from
      // scrollHeight — capture them via the element's rendered bottom.
      let rectBottom = 0
      const kids = b.children
      if (kids && kids.length) {
        const last = kids[kids.length - 1]
        if (last && last.getBoundingClientRect) {
          rectBottom = Math.ceil(last.getBoundingClientRect().bottom + (window.scrollY || 0))
        }
      }
      return Math.max(
        b.scrollHeight, b.offsetHeight,
        d.scrollHeight, d.offsetHeight,
        Math.ceil(d.getBoundingClientRect().height),
        rectBottom
      )
    }
    const post = () => {
      const height = measure()
      if (height > 0 && height !== lastPosted) {
        lastPosted = height
        window.parent.postMessage({ type: 'integolf-embed-resize', height }, '*')
      }
    }
    const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(post) }

    const ro = new ResizeObserver(schedule)
    ro.observe(document.body)
    window.addEventListener('resize', schedule)
    window.addEventListener('load', schedule)
    // Image load events don't bubble — capture phase catches them so the height
    // re-syncs once photos / hero images paint in.
    document.addEventListener('load', schedule, true)
    // Web fonts (per-grouping branding) reflow text height when they swap in.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(schedule).catch(() => {})
    }
    // Settle loop — re-measure a handful of times over ~4s to catch any late
    // reflow no single event fired for. Idempotent (post() only re-sends on a
    // real change), so this costs at most a few extra postMessages.
    const timers = [100, 350, 700, 1200, 2000, 3000, 4000].map(t => setTimeout(schedule, t))
    schedule()

    return () => {
      ro.disconnect()
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('load', schedule)
      document.removeEventListener('load', schedule, true)
      timers.forEach(clearTimeout)
    }
  }, [])
}
